import React, { useState, useCallback } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
// Components
import CentsLoader from '../../common/centsLoader/CentsLoader';
import { uploadCustomerList } from '../../../api/admin/customers/customers';

const UploadCustomers = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [step, setStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [shouldValidateStripeCustomer, setShouldValidateStripeCustomer] = useState(false);

  const history = useHistory();

  /**
   * Handle file drag and drop
   */
  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.onload = () => {
        // Do whatever you want with the file contents
        setSelectedFile(reader.result);
        setSelectedFileName(file.path);
      };

      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  /**
   * Set the selected business in state
   */
  const handleBusinessSelection = (businessId) => {
    setSelectedBusinessId(businessId);
  };

  /**
   * Move forward in the form
   */
  const moveToNextStep = () => {
    setStep(step + 1);
    setErrorMessage(null);
  };

  /**
   * Move to the previous step in the form
   */
  const moveToPreviousStep = () => {
    setStep(step - 1);
    setSelectedFile(null);
    setSelectedFileName(null);
    setShouldValidateStripeCustomer(false);
    setErrorMessage(null);
  };

  /**
   * Upload the list of customers and store them to the business
   */
  const saveCustomerList = async () => {
    try {
      setLoading(true);

      const params = {
        businessId: selectedBusinessId,
        fileToUpload: selectedFile,
        shouldValidateStripeCustomer,
      };

      const uploadData = await uploadCustomerList(params);
      const invalidPhoneNumbers = uploadData.data.invalidPhoneNumbers || [];
      if (uploadData.data.success) {
        if (!!invalidPhoneNumbers.length) {
          setErrorMessage('Potential invalid phone numbers: ' + invalidPhoneNumbers.join(', '));
          setSelectedFileName(null);
          setSelectedFile(null);
        } else {
          setLoading(false);
          history.push('/customers');
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorMessage(error.response.data.error);
    }
  };

  /**
   * Render the UI to display business selection form
   */
  const renderBusinessSelection = () => {
    return (
      <>
        <h1>Select Business</h1>
        <div className="upload-customers-form-row">
          <p className="label">Business Name</p>
          <BusinessPicker
            id="business-selector"
            selectedBusinessId={selectedBusinessId}
            onChange={handleBusinessSelection}
            label="Select a Business"
          />
        </div>
        <div className="submit-button-container">
          <button
            className={'submit-button ' + (!selectedBusinessId ? 'disabled' : 'active')}
            onClick={moveToNextStep}
          >
            Next
          </button>
          {errorMessage && <p className="error-text">{errorMessage}</p>}
        </div>
      </>
    );
  };

  /**
   * Render the UI to upload customer CSV information
   */
  const renderUploadFileSelection = () => {
    return (
      <>
        <h1>Select Your File</h1>
        <p>
          Please note: the file must be a CSV and must include the following headers EXACTLY as they
          appear:
        </p>
        <ol>
          <li>firstName</li>
          <li>lastName</li>
          <li>phoneNumber</li>
          <li>email</li>
          <li>address1 (optional)</li>
          <li>address2 (optional)</li>
          <li>city (optional)</li>
          <li>state (optional)</li>
          <li>postalCode (optional)</li>
          <li>preferences (optional) (Section::option|Section::option|Section::option2)</li>
          <li>preferencesNotes (optional)</li>
          <li>credits (optional)</li>
          <li>stripeCustomerId (optional)</li>
          <li>optedInMarketingSms (optional)</li>
          <li>tag</li>
        </ol>
        <p>
          If the CSV you are attaching does not have formatting in place that matches the exact
          headers mentioned above, the upload process will fail.
        </p>
        <div className="upload-customers-form-row">
          <p className="label">File</p>
          <div className="open-picker-container">
            {!selectedFileName && (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>Drop the files here ...</p>
                ) : (
                  <p>Drag and drop or click to select files</p>
                )}
              </div>
            )}
            {selectedFileName && <p style={{ textAlign: 'center' }}>{selectedFileName}</p>}
          </div>
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={shouldValidateStripeCustomer}
                onChange={(e) => setShouldValidateStripeCustomer(e.target.checked)}
              />
            }
            label={
              <span className="checkbox-label">
                Perform <code>stripeCustomerId</code> validation{' '}
                <Tooltip title="Verify that Stripe Customers linked via 'stripeCustomerId' have an email that matches the customer email in the CSV. Invalid 'stripeCustomerId' values won't be filled on the customer record.">
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="About stripeCustomerId validation"
                    // clicking it should not toggle the checkbox
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </span>
                </Tooltip>
              </span>
            }
          />
        </div>
        <div className="submit-button-container">
          <button className="submit-button active" onClick={moveToPreviousStep}>
            Back
          </button>
          <button
            className={'submit-button ' + (!selectedFile ? 'disabled' : 'active')}
            onClick={saveCustomerList}
          >
            Submit
          </button>
        </div>
        {errorMessage && (
          <div className="error-container">
            <p className="error-text">{errorMessage}</p>
          </div>
        )}
      </>
    );
  };

  /**
   * Determine which form element to show based on step
   */
  const renderUploadForm = () => {
    switch (step) {
      case 1:
        return renderBusinessSelection();
      case 2:
        return renderUploadFileSelection();
      default:
        break;
    }
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="upload-customer-container">
          <div className="upload-customers-header">
            <h1>Upload Customers</h1>
          </div>
          <div className="upload-customers-form-container">
            <div className="upload-customers-form">{renderUploadForm()}</div>
          </div>
        </div>
      )}
    </>
  );
};

UploadCustomers.displayName = 'UploadCustomers';

export default UploadCustomers;
